import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import {
  trackOnboardingStepView,
  trackOnboardingStepComplete,
  trackOnboardingSkipped,
  trackOnboardingCompleted,
} from '../utils/analytics';

// ── Interest tiles shown on the single setup screen ──────────────────────────
const INTEREST_TILES = [
  { label: 'Dinosaurs',   emoji: '🦕' },
  { label: 'Space',       emoji: '🚀' },
  { label: 'Unicorns',    emoji: '🦄' },
  { label: 'Animals',     emoji: '🐾' },
  { label: 'Superheroes', emoji: '🦸' },
  { label: 'Fairies',     emoji: '🧚' },
  { label: 'Pirates',     emoji: '🏴‍☠️' },
  { label: 'Dragons',     emoji: '🐉' },
  { label: 'Ocean',       emoji: '🌊' },
  { label: 'Magic',       emoji: '✨' },
  { label: 'Robots',      emoji: '🤖' },
  { label: 'Jungle',      emoji: '🌿' },
];

const AVATARS = ['🧒','👦','👧','🧑','👶','🦸','🧙','🧜','🦄','🌟','🦁','🐻','🦊','🐼','🐨','🦋','🐸','🐶','🐱','🐭','🐰'];

// ── Progress messages shown while story generates ────────────────────────────
const PROGRESS_MESSAGES = [
  { pct: 0,  msg: 'Starting the adventure…' },
  { pct: 15, msg: 'Dreaming up the perfect plot…' },
  { pct: 30, msg: "Adding your child's favourite things…" },
  { pct: 45, msg: 'Writing the exciting scenes…' },
  { pct: 60, msg: 'Giving the characters their voices…' },
  { pct: 75, msg: 'Polishing every word…' },
  { pct: 88, msg: 'Creating the illustration…' },
  { pct: 95, msg: 'Almost ready — putting on the finishing touches…' },
];

// ── Tiny confetti burst ───────────────────────────────────────────────────────
function ConfettiBurst() {
  const pieces = Array.from({ length: 32 }, (_, i) => ({
    id: i,
    color: ['#6f42c1','#f9c74f','#4ade80','#fb923c','#38bdf8','#f472b6'][i % 6],
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 0.6}s`,
    duration: `${0.8 + Math.random() * 0.8}s`,
    size: `${6 + Math.random() * 8}px`,
    rotate: `${Math.random() * 720}deg`,
  }));
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 9999, overflow: 'hidden' }}>
      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(var(--r)); opacity: 0; }
        }
      `}</style>
      {pieces.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          top: '-20px',
          left: p.left,
          width: p.size,
          height: p.size,
          background: p.color,
          borderRadius: '2px',
          '--r': p.rotate,
          animation: `confettiFall ${p.duration} ${p.delay} ease-in forwards`,
        }} />
      ))}
    </div>
  );
}

const OnboardingPage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  // ── Step state ────────────────────────────────────────────────────────────
  // 1 = single setup screen · 2 = generating the first story
  const [step, setStep] = useState(1);

  // Child info (single screen)
  const [childName, setChildName]       = useState('');
  const [childAge, setChildAge]         = useState('');
  const [childGender, setChildGender]   = useState('neutral');
  const [selectedAvatar, setSelectedAvatar] = useState('🧒');
  const [interests, setInterests]       = useState([]);

  // Bedtime reminder (optional — kept compact so it never blocks the story)
  const [enableReminder, setEnableReminder] = useState(true);
  const [bedtimeTime, setBedtimeTime]   = useState('20:00');

  // Generating
  const [progress, setProgress]         = useState(0);
  const [progressMsg, setProgressMsg]   = useState(PROGRESS_MESSAGES[0].msg);
  const [generating, setGenerating]     = useState(false);
  const [error, setError]               = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const progressRef                     = useRef(null);
  const timerRef                        = useRef(null);
  // Survives a failed attempt so a retry reuses the child instead of creating a
  // second one and tripping the free plan's 1-child limit.
  const createdChildRef                 = useRef(null);

  // First story always uses the gentle, magical default tone; parents can
  // change the tone per-story afterwards on the Generate page.
  const DEFAULT_TONE = 'magical';

  // ── Guard: skip onboarding if already done or user has children ─────────
  useEffect(() => {
    if (user?.hasCompletedOnboarding) {
      navigate('/dashboard', { replace: true });
      return;
    }
    API.get('/children').then(res => {
      if (res.data.children?.length > 0) {
        API.put('/auth/complete-onboarding').catch(() => {});
        updateUser({ ...user, hasCompletedOnboarding: true });
        navigate('/dashboard', { replace: true });
      }
    }).catch(() => {});
  }, []);

  // ── Cleanup timers on unmount ─────────────────────────────────────────────
  useEffect(() => () => {
    clearInterval(progressRef.current);
    clearTimeout(timerRef.current);
  }, []);

  // ── Track step views ──────────────────────────────────────────────────────
  useEffect(() => {
    trackOnboardingStepView(step);
  }, [step]);

  // ── Interest toggle ───────────────────────────────────────────────────────
  const toggleInterest = (label) => {
    setInterests(prev =>
      prev.includes(label) ? prev.filter(i => i !== label) : [...prev, label].slice(0, 5)
    );
  };

  // ── Create child, save reminder, generate first story ────────────────────
  const startGeneration = async () => {
    // Validate the only two required fields, then go straight to the story.
    if (!childName.trim()) return setError("Please enter your child's name.");
    if (!childAge || childAge < 2 || childAge > 12) return setError('Please enter an age between 2 and 12.');

    setError('');
    trackOnboardingStepComplete(1);
    setStep(2);
    setGenerating(true);
    setProgress(0);
    setProgressMsg(PROGRESS_MESSAGES[0].msg);

    // Animate progress bar independently of the actual API call
    let currentPct = 0;
    let msgIdx = 0;
    progressRef.current = setInterval(() => {
      // Slow down as we approach 90 — real story generation takes 15-30s
      const increment = currentPct < 60 ? 2.5 : currentPct < 85 ? 0.8 : 0.2;
      currentPct = Math.min(currentPct + increment, 93);
      setProgress(currentPct);
      // Advance message
      const next = PROGRESS_MESSAGES.findIndex(m => m.pct > currentPct);
      const idx  = next === -1 ? PROGRESS_MESSAGES.length - 1 : next - 1;
      if (idx !== msgIdx) { msgIdx = idx; setProgressMsg(PROGRESS_MESSAGES[idx].msg); }
    }, 400);

    try {
      // 1. Create the child profile — or REUSE the one an earlier attempt made.
      //
      // Story generation can fail for ordinary reasons (the generate endpoint is
      // rate limited to 3 per 10 minutes, plus AI/network hiccups). This handler
      // sends the user back to step 1 to retry — and a retry used to POST
      // /children again. On the free plan that hits the 1-child limit and returns
      // "Free plan supports 1 child profile. Upgrade to add more.", so a single
      // failed generation left a brand-new user permanently unable to finish
      // onboarding, being told to upgrade. Reuse and update instead.
      const childPayload = {
        name:     childName.trim(),
        age:      Number(childAge),
        gender:   childGender,
        avatar:   selectedAvatar,
        interests,
        storyPreferences: { tone: DEFAULT_TONE, length: 'medium' },
      };

      let child = createdChildRef.current;
      if (child?._id) {
        // Retry: keep the same profile but apply any edits they just made.
        const updated = await API.put(`/children/${child._id}`, childPayload).catch(() => null);
        child = updated?.data?.child || child;
      } else {
        const childRes = await API.post('/children', childPayload);
        child = childRes.data.child;
      }
      createdChildRef.current = child;

      // 2. Save bedtime reminder (fire-and-forget — don't block story gen)
      if (enableReminder) {
        API.put('/reminders/settings', {
          enabled:      true,
          time:         bedtimeTime,
          emailEnabled: true,
          timezone:     Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
        }).catch(() => {});
      }

      // 3. Generate first story
      const storyRes = await API.post('/stories/generate', {
        childId:  child._id,
        interests,
        tone:     DEFAULT_TONE,
        themes:   [],
        language: 'en',
      });

      // 4. Fill bar to 100 and celebrate
      clearInterval(progressRef.current);
      setProgress(100);
      setProgressMsg('Your story is ready! 🎉');
      setShowConfetti(true);
      trackOnboardingCompleted({ tone: DEFAULT_TONE, interests_count: interests.length, reminder_enabled: enableReminder });
      await API.put('/auth/complete-onboarding').catch(() => {});
      updateUser({ ...user, hasCompletedOnboarding: true });

      timerRef.current = setTimeout(() => {
        // The API responds { storyId, generating } — there is no `story` object.
        // Reading story?._id gave undefined, so every new user was navigated to
        // /dashboard/story/undefined and silently bounced to an empty library.
        // `generating=true` is required so the reader polls instead of fetching
        // a story that is still being written.
        navigate(
          `/dashboard/story/${storyRes.data.storyId}?new=true&generating=true&firstStory=true`,
          { replace: true }
        );
      }, 1200);
    } catch (err) {
      clearInterval(progressRef.current);
      setGenerating(false);
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
      setStep(1); // Let them retry from the setup screen
    }
  };

  // ── Shared styles ─────────────────────────────────────────────────────────
  const card = {
    background: 'white',
    borderRadius: '24px',
    padding: '40px',
    boxShadow: '0 20px 60px rgba(45,27,105,0.12)',
    width: '100%',
    maxWidth: '520px',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg,#1e1048 0%,#2d1b69 60%,#4a2a9e 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
    }}>
      {showConfetti && <ConfettiBurst />}

      {/* Logo */}
      <div style={{ marginBottom: '28px', textAlign: 'center' }}>
        <span style={{ fontFamily: "'Fredoka One',cursive", fontSize: '28px', color: 'white' }}>
          🌙 Dreamy Bedtime
        </span>
      </div>

      {/* ── STEP 1: Single setup screen ────────────────────────────────────── */}
      {step === 1 && (
        <div style={card}>
          <h2 style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', marginBottom: '6px', fontSize: '26px' }}>
            Let's make your first story
          </h2>
          <p style={{ color: 'var(--text-muted2)', marginBottom: '24px', fontSize: '14px' }}>
            Just a name and age — we'll write a personalised bedtime story in about 20 seconds. Everything else is optional.
          </p>

          {error && (
            <div className="alert alert-danger py-2 small mb-3" style={{ borderRadius: '10px' }}>
              {error}
            </div>
          )}

          {/* Name */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-main)', marginBottom: '6px', display: 'block' }}>
              Child's first name
            </label>
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder="e.g. Emma"
              style={{ borderRadius: '12px' }}
              value={childName}
              onChange={e => setChildName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && startGeneration()}
              autoFocus
            />
          </div>

          {/* Age + Gender */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-main)', marginBottom: '6px', display: 'block' }}>Age</label>
              <input
                type="number"
                className="form-control form-control-lg"
                placeholder="e.g. 5"
                min={2} max={12}
                style={{ borderRadius: '12px' }}
                value={childAge}
                onChange={e => setChildAge(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && startGeneration()}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-main)', marginBottom: '6px', display: 'block' }}>Gender</label>
              <select
                className="form-select form-select-lg"
                style={{ borderRadius: '12px' }}
                value={childGender}
                onChange={e => setChildGender(e.target.value)}
              >
                <option value="neutral">Prefer not to say</option>
                <option value="boy">Boy</option>
                <option value="girl">Girl</option>
              </select>
            </div>
          </div>

          {/* Avatar picker (optional) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-main)', marginBottom: '8px', display: 'block' }}>
              Pick an avatar <span style={{ color: 'var(--text-soft)', fontWeight: 400 }}>(optional)</span>
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {AVATARS.map(av => (
                <button key={av} onClick={() => setSelectedAvatar(av)} style={{
                  width: '44px', height: '44px', borderRadius: '50%', fontSize: '22px',
                  border: selectedAvatar === av ? '3px solid #6f42c1' : '2px solid var(--border-purple)',
                  background: selectedAvatar === av ? 'var(--purple-section)' : 'var(--card-bg)',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}>{av}</button>
              ))}
            </div>
          </div>

          {/* Interests (optional — improves personalisation but never required) */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-main)', marginBottom: '8px', display: 'block' }}>
              What does {childName || 'your child'} love? <span style={{ color: 'var(--text-soft)', fontWeight: 400 }}>(optional — pick up to 5)</span>
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {INTEREST_TILES.map(({ label, emoji }) => {
                const selected = interests.includes(label);
                return (
                  <button key={label} onClick={() => toggleInterest(label)} style={{
                    padding: '7px 14px',
                    borderRadius: '20px',
                    border: selected ? '2px solid #6f42c1' : '2px solid #e5e7eb',
                    background: selected ? '#f0e8ff' : 'white',
                    color: selected ? '#6f42c1' : '#374151',
                    fontWeight: selected ? 600 : 400,
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}>
                    {emoji} {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bedtime reminder (compact, optional) */}
          <div style={{
            background: enableReminder ? '#f0e8ff' : '#f9fafb',
            borderRadius: '16px',
            padding: '14px 16px',
            marginBottom: '24px',
            border: `2px solid ${enableReminder ? '#6f42c1' : '#e5e7eb'}`,
            transition: 'all 0.2s',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ paddingRight: '10px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-main)' }}>
                  🔔 Set a bedtime habit
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted2)', marginTop: '2px' }}>
                  "After I say goodnight, I open Dreamy Bedtime" — we'll remind you at the right moment
                </div>
              </div>
              <div
                onClick={() => setEnableReminder(v => !v)}
                style={{
                  width: '44px', height: '24px', borderRadius: '12px',
                  background: enableReminder ? '#6f42c1' : '#d1d5db',
                  position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: '2px',
                  left: enableReminder ? '22px' : '2px',
                  width: '20px', height: '20px', borderRadius: '50%',
                  background: 'white', transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
            </div>
            {enableReminder && (
              <div style={{ marginTop: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#6f42c1', marginBottom: '6px', display: 'block' }}>
                  What time is {childName || 'your child'}'s bedtime?
                </label>
                <input
                  type="time"
                  className="form-control"
                  style={{ borderRadius: '10px', borderColor: '#6f42c1', maxWidth: '150px' }}
                  value={bedtimeTime}
                  onChange={e => setBedtimeTime(e.target.value)}
                />
              </div>
            )}
          </div>

          <button
            className="btn btn-lg w-100 fw-bold rounded-pill"
            style={{ background: 'linear-gradient(135deg,#6f42c1,#9b6fe8)', color: 'white', padding: '14px' }}
            onClick={startGeneration}
          >
            <i className="bi bi-magic me-2"></i>Create {childName ? `${childName}'s` : 'my'} first story!
          </button>
        </div>
      )}

      {/* ── STEP 2: Generating ─────────────────────────────────────────────── */}
      {step === 2 && (
        <div style={{ ...card, textAlign: 'center' }}>
          {/* Animated moon */}
          <div style={{ fontSize: '72px', marginBottom: '16px', animation: 'pulse 2s ease-in-out infinite' }}>
            🌙
          </div>
          <style>{`
            @keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.08); } }
          `}</style>

          <h2 style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: '26px', marginBottom: '8px' }}>
            Creating {childName}'s first story…
          </h2>
          <p style={{ color: 'var(--text-muted2)', fontSize: '14px', marginBottom: '28px' }}>
            Sit tight — this usually takes about 20 seconds.
          </p>

          {/* Progress bar */}
          <div style={{ background: 'var(--purple-section)', borderRadius: '999px', height: '12px', marginBottom: '12px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              borderRadius: '999px',
              background: 'linear-gradient(90deg,#6f42c1,#9b6fe8)',
              width: `${progress}%`,
              transition: 'width 0.4s ease',
            }} />
          </div>

          <p style={{ color: '#6f42c1', fontWeight: 600, fontSize: '14px', minHeight: '20px' }}>
            {progressMsg}
          </p>

          {error && (
            <div className="alert alert-danger py-2 small mt-3" style={{ borderRadius: '10px' }}>
              {error}
              <button className="btn btn-sm btn-danger ms-2 rounded-pill" onClick={() => startGeneration()}>
                Try again
              </button>
            </div>
          )}

          {/* What's happening cards */}
          {!error && (
            <div style={{ marginTop: '28px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', textAlign: 'center' }}>
              {[
                { emoji: '✍️', label: 'Writing the story' },
                { emoji: '🎨', label: 'Creating illustration' },
                { emoji: '📚', label: 'Saving to library' },
              ].map(({ emoji, label }) => (
                <div key={label} style={{ padding: '12px 8px', background: 'var(--header-bg)', borderRadius: '12px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '4px' }}>{emoji}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted2)', lineHeight: 1.3 }}>{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Skip link */}
      {step === 1 && (
        <button
          onClick={() => {
            trackOnboardingSkipped(step);
            API.put('/auth/complete-onboarding').catch(() => {});
            updateUser({ ...user, hasCompletedOnboarding: true });
            navigate('/dashboard/children');
          }}
          style={{
            marginTop: '16px', background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.5)', fontSize: '13px', cursor: 'pointer',
          }}
        >
          Skip setup — I'll do this later
        </button>
      )}
    </div>
  );
};

export default OnboardingPage;
